/**
 * Per-attribute "last selected duration unit" storage.
 *
 * The backend models duration attributes (e.g. `runningTimeDays`) as plain numbers
 * stored in hours and carries no unit. The {@link NumberWithUnitInput} lets the user
 * enter the value in Days or Hours; we remember their last choice per attribute index
 * here so it pre-selects next time. Stored as a single JSON map under one key.
 */

export type DurationUnit = 'days' | 'hours';

const STORAGE_KEY = 'attributeDurationUnits';

type DurationUnitMap = Record<string, DurationUnit>;

const loadMap = (): DurationUnitMap => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

/** Returns the last-selected unit for an attribute index, or null if none stored. */
export const getAttributeUnit = (index: string): DurationUnit | null => {
    const unit = loadMap()[index];
    return unit === 'days' || unit === 'hours' ? unit : null;
};

/** Persists the selected unit for an attribute index (read-merge-write). */
export const setAttributeUnit = (index: string, unit: DurationUnit): void => {
    try {
        const map = loadMap();
        map[index] = unit;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        // ignore storage errors (e.g. private mode)
    }
};
